import { useEffect, useRef, useState } from "react";
import { useRouter } from "@tanstack/react-router";
import { useImportTemplateMutation } from "@/api/templates";
import { ImageToVectorDialog } from "@/components/prompt/ImageToVectorDialog";
import type { ProjectKind } from "@/types/prompt";

/** One-shot hint read by the /create workspace to seed a brand-new session
 *  with the chosen project kind. Consumed (removed) on the first mount. */
export const NEW_PROJECT_KIND_KEY = "motionforge-new-project-kind";

/**
 * Single primary action menu replacing the wall of "Create Template" /
 * "Import Template" buttons. Pattern matches Google Drive's "+ New" —
 * one button, click reveals all create-flavored actions.
 *
 * "New Folder" is intentionally listed but disabled with a tooltip —
 * the folders backend (table + endpoints) is a separate PR. Listing it
 * now means the visual layout is forward-compatible: when folders ship,
 * we just remove the disabled flag.
 */
export function NewMenu() {
  const [open, setOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const importMutation = useImportTemplateMutation();

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  // Carry the chosen kind to /create via a one-shot sessionStorage hint, then
  // navigate. The workspace's new-session seed reads and clears it.
  const startCreate = (kind: ProjectKind) => {
    setOpen(false);
    try {
      sessionStorage.setItem(NEW_PROJECT_KIND_KEY, kind);
    } catch {
      // storage disabled — workspace falls back to remotion
    }
    void router.navigate({ to: "/create" });
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result as string);
        importMutation.mutate(data, {
          onSuccess: (template) => {
            void router.navigate({
              to: "/templates/$id",
              params: { id: template.id },
            });
          },
        });
      } catch {
        alert("Invalid template file");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  return (
    <div ref={ref} className="relative">
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,.motionforge.json"
        onChange={handleImport}
        className="hidden"
      />
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={importMutation.isPending}
        className="flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:opacity-40"
      >
        <span className="text-base">+</span>
        <span>{importMutation.isPending ? "Importing…" : "New"}</span>
        <span className="text-[10px]">▾</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 min-w-[200px] overflow-hidden rounded-lg border border-line bg-surface py-1 shadow-lg">
          <MenuItem
            label="Motion graphic"
            hint="Animated Remotion video"
            onClick={() => startCreate("remotion")}
          />
          <MenuItem
            label="SVG graphic"
            hint="Static vector image"
            onClick={() => startCreate("svg")}
          />
          <MenuItem
            label="Image to vector"
            hint="Trace or pixel-art from an image"
            onClick={() => {
              setOpen(false);
              setImageDialogOpen(true);
            }}
          />
          <MenuItem
            label="Import Template"
            hint="From a .motionforge.json file"
            onClick={() => {
              setOpen(false);
              fileInputRef.current?.click();
            }}
          />
          <div className="my-1 border-t border-line" />
          <MenuItem
            label="New Folder"
            hint="Coming soon"
            disabled
            onClick={() => {}}
          />
        </div>
      )}
      <ImageToVectorDialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        // The dialog already stashed the handoff + kind hint; route into a
        // fresh session exactly like the kind choices above.
        onConverted={() => void router.navigate({ to: "/create" })}
      />
    </div>
  );
}

function MenuItem({
  label,
  hint,
  onClick,
  disabled,
}: {
  label: string;
  hint?: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={disabled && hint ? hint : undefined}
      className="block w-full px-3 py-1.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 hover:bg-surface-3 disabled:hover:bg-transparent"
    >
      <div className="text-sm font-medium text-ink">
        {label}
      </div>
      {hint && (
        <div className="text-xs text-ink-3">{hint}</div>
      )}
    </button>
  );
}
